import { Button, HStack, VStack } from '@chakra-ui/react';
import { selectCurrentUserSubscriptionPlan, useAuthStore } from '@waitroom/auth';
import { getTrialDaysLeft, isFreeTrialPlan } from '@waitroom/common';
import { differenceInWeeks } from 'date-fns';
import { ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { updateAppState, useAppStore } from '../../../store/store';
import Modal, { ModalProps } from '../Modal';

export type FreeTrialEndModalProps = Omit<ModalProps, 'children'>;

const FreeTrialEndModal = ({ isOpen, onClose, ...rest }: FreeTrialEndModalProps): ReactNode => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const upgradePagePath = '/dashboard/subscription';
  const isUpgrading = pathname === upgradePagePath;
  const currentPlan = useAuthStore(selectCurrentUserSubscriptionPlan);
  const isFreeTrial = isFreeTrialPlan(currentPlan);
  const isTrialOver = Boolean(isFreeTrial && getTrialDaysLeft(currentPlan) === 0);
  const upgradeDismissedOn = useAppStore.use.upgradeDismissedOn();
  const hasBeenTwoWeeks = upgradeDismissedOn
    ? differenceInWeeks(new Date(), upgradeDismissedOn) >= 2
    : false;

  const onClickDismiss = useCallback(() => {
    const nowInISO = new Date().toISOString();
    updateAppState({ upgradeDismissedOn: nowInISO });
    onClose?.();
  }, [onClose]);

  const onClickUpgrade = useCallback(() => {
    navigate(upgradePagePath);
  }, [navigate]);

  if (!isFreeTrial || !isTrialOver || (upgradeDismissedOn && !hasBeenTwoWeeks) || isUpgrading)
    return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} {...rest}>
      <Modal.Header my={4} closeButtonProps={{ size: 'xl', p: 3 }}>
        {t('pricing.freeTrial.trialEnded')}
      </Modal.Header>
      <Modal.Body>
        <VStack gap={4} align={'start'}>
          <p>{t('pricing.freeTrial.trialEndedMessage')}</p>
          <p>{t('pricing.freeTrial.trialEndedUpgrade')}</p>
        </VStack>
      </Modal.Body>
      <Modal.Footer my={4}>
        <HStack w="full" justify={'space-evenly'} gap={5}>
          <Button w="full" variant="outline" onClick={onClickDismiss}>
            {t('pricing.freeTrial.stayWithFreePlan')}
          </Button>
          <Button w="full" onClick={onClickUpgrade}>
            {t('pricing.freeTrial.upgradeNow')}
          </Button>
        </HStack>
      </Modal.Footer>
    </Modal>
  );
};

export default FreeTrialEndModal;
